import {
  TextField,
  InputAdornment,
  IconButton,
  useTheme,
  Select,
  MenuItem,
  Button,
  Typography,
  Box,
  Stack,
} from "@mui/material";
import {
  CSSProperties,
  ReactNode,
  useCallback,
  useEffect,
  forwardRef,
  useRef,
  useState,
  useImperativeHandle,
} from "react";

import { isEmpty, pickBy } from "lodash";
import { useQuery } from "@tanstack/react-query";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { motion, AnimatePresence } from "framer-motion";
import { useFormik } from "formik";

import { useNavigate } from "react-router-dom";
import queryString from "query-string";

import styles from "./SearchBar.module.css";
import APICompanyMatchProfile from "../../types/APICompanyMatchProfile";
import { useTrueBizApi } from "../../api";
import { useShowNetworkFailureToast } from "../NetworkFailureToast";

export interface Ref {
  search: () => Promise<void>;
  reset: () => Promise<void>;
}

export interface Props {
  onSearch?: (searchValues: {
    domain?: string | null;
    businessName?: string;
    description?: string;
    email?: string;
    phone?: string;
    applicantFullName?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    stateProvince?: string;
    postalCode?: string;
    country?: string;
    matchProfileId?: UUID;
    externalTrackingRef?: string;
  }) => Promise<void>;
  domain?: string | null;
  advanced?: {
    businessName?: string;
    description?: string;
    email?: string;
    phone?: string;
    applicantFullName?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    stateProvince?: string;
    postalCode?: string;
    country?: string;
    externalTrackingRef?: string;
  };
  matchProfileId?: UUID | null | undefined;
  matchProfiles?: APICompanyMatchProfile[];

  children?: ReactNode;
  style?: CSSProperties;
  autoFocus?: boolean;
  fullWidth?: boolean;
  allowAdvanced?: boolean;
  childrenPosition?: "top" | "right";
  errorReason?: string;

  onChange?: (value: string) => void;
}

const SearchBar = forwardRef<Ref, Props>(function SearchBar(
  {
    onSearch,
    domain: initialDomain,
    advanced: initialAdvanced,
    matchProfileId: initialMatchProfileId,
    matchProfiles: initialMatchProfiles,
    children,
    childrenPosition = "top",
    style,
    autoFocus = true,
    fullWidth = false,
    allowAdvanced = true,
    errorReason,
    onChange,
  }: Props,
  ref
) {
  const navigate = useNavigate();

  const api = useTrueBizApi();
  const domainInputRef = useRef<HTMLInputElement | null>(null);
  const domainWrapperRef = useRef<HTMLDivElement | null>(null);

  const theme = useTheme();

  const showToast = useShowNetworkFailureToast();

  const [domain, setDomain] = useState(initialDomain);

  const matchProfileQuery = useQuery({
    queryKey: ["getCompanyMatchProfiles"],
    queryFn: () => api.getCompanyMatchProfiles(),
    enabled: initialMatchProfiles === undefined,
  });

  const matchProfiles = initialMatchProfiles || matchProfileQuery.data;
  const [matchProfileId, setMatchProfileId] = useState<UUID | "">(
    initialMatchProfileId || ""
  );

  useEffect(() => {
    if (!matchProfiles) return;
    if (initialMatchProfileId) return;

    const accountDefault = matchProfiles.find(
      (mp) => mp.is_account_default === true
    );
    setMatchProfileId(accountDefault ? accountDefault.id || "" : "");
  }, [matchProfiles, initialMatchProfileId]);

  const [showAdvanced, setShowAdvanced] = useState(
    allowAdvanced ? !isEmpty(initialAdvanced) : false
  );

  const advancedSearchForm = useFormik({
    initialValues: {
      businessName: initialAdvanced?.businessName || "",
      description: initialAdvanced?.description || "",
      email: initialAdvanced?.email || "",
      phone: initialAdvanced?.phone || "",
      applicantFullName: initialAdvanced?.applicantFullName || "",
      addressLine1: initialAdvanced?.addressLine1 || "",
      addressLine2: initialAdvanced?.addressLine2 || "",
      city: initialAdvanced?.city || "",
      stateProvince: initialAdvanced?.stateProvince || "",
      postalCode: initialAdvanced?.postalCode || "",
      country: initialAdvanced?.country || "",
      externalTrackingRef: initialAdvanced?.externalTrackingRef || "",
    },
    onSubmit: () => {
      search();
    },
  });

  const playErrorAnimation = useCallback(
    () =>
      new Promise<void>((resolve, reject) => {
        if (!domainWrapperRef.current) return resolve();
        const el = domainWrapperRef.current;

        const listen = (e: AnimationEvent) => {
          if (e.animationName === styles.shake) {
            el.removeEventListener("animationend", listen);
            resolve();
          }
        };

        el.addEventListener("animationend", listen);

        if (el.classList.contains(styles.invalid)) {
          domainWrapperRef.current.classList.remove(styles.invalid);
        }

        // ensure that the animation (re-)plays
        requestAnimationFrame(() => {
          el.classList.add(styles.invalid);
        });
      }),
    []
  );

  const advancedSearchFormValues = advancedSearchForm.values; // this is stupid but it keeps eslint happy

  const search = useCallback(async () => {
    if (!showAdvanced && !domain) {
      await playErrorAnimation();
      domainInputRef.current?.focus();
      return;
    }

    if (showAdvanced && !domain) {
      // if no domain is provided both name and country must be filled out
      if (!advancedSearchFormValues.businessName) {
        showToast("If no domain is provided, a business name is required.");
        return;
      }
    }

    if (onSearch) {
      await onSearch({
        domain,
        ...advancedSearchFormValues,
        matchProfileId,
      });
    } else {
      navigate(
        `/?${queryString.stringify(
          pickBy({
            domain,
            ...advancedSearchFormValues,
            matchProfileId,
            run: 1,
          })
        )}`
      );
    }
  }, [
    playErrorAnimation,
    domain,
    matchProfileId,
    onSearch,
    navigate,
    showToast,
    showAdvanced,
    advancedSearchFormValues,
  ]);

  const reset = useCallback(async () => {
    navigate("/", { replace: true });

    setShowAdvanced(false);
    setDomain("");
    advancedSearchForm.resetForm({
      values: {
        businessName: "",
        description: "",
        email: "",
        phone: "",
        applicantFullName: "",
        addressLine1: "",
        addressLine2: "",
        city: "",
        stateProvince: "",
        postalCode: "",
        country: "",
        externalTrackingRef: "",
      },
    });
  }, [navigate, advancedSearchForm.resetForm]); // eslint-disable-line react-hooks/exhaustive-deps

  useImperativeHandle(
    ref,
    () => ({
      search,
      reset,
    }),
    [search, reset]
  );

  useEffect(() => {
    if (!showAdvanced) {
      advancedSearchForm.resetForm();
    }
  }, [showAdvanced, advancedSearchForm.resetForm]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!autoFocus) return;
    if (showAdvanced) return;
    domainInputRef.current?.focus();
  }, [autoFocus, showAdvanced]);

  return (
    <form
      style={{
        marginTop: 25,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        ...(style || {}),
      }}
      onSubmit={(e) => {
        if (showAdvanced) {
          advancedSearchForm.handleSubmit(e);
        } else {
          e.preventDefault();
          search();
        }
      }}
    >
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            maxWidth: fullWidth ? undefined : "550px",
            position: "relative",
          }}
        >
          {children && childrenPosition === "top" ? children : null}
          <AnimatePresence>
            {errorReason && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, height: 0 }}
                style={{ overflow: "hidden" }}
              >
                <Typography
                  variant="body2"
                  color="error"
                  sx={{
                    mb: 1,
                    textAlign: "center",
                  }}
                >
                  {errorReason}
                </Typography>
              </motion.div>
            )}
          </AnimatePresence>
          <TextField
            inputRef={domainInputRef}
            ref={domainWrapperRef}
            value={domain}
            label={showAdvanced ? "Website" : undefined}
            placeholder="www."
            fullWidth
            autoFocus={autoFocus}
            title="Search for a business"
            sx={{
              "&:hover:not(.Mui-focused)": {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.primary.main,
                },
              },
            }}
            onChange={(e) => {
              setDomain(e.target.value);
              onChange?.(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                search();
              }
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      tabIndex={3}
                      onClick={() => search()}
                      sx={{ padding: "4px" }}
                    >
                      <SearchOutlinedIcon color="primary" />
                    </IconButton>
                  </InputAdornment>
                ),
              },

              htmlInput: {
                tabIndex: 1,
              },
            }}
          />
          {allowAdvanced && (
            <div
              style={{
                width: "100%",
                paddingTop: "0.5em",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Button
                size="small"
                onClick={() => setShowAdvanced((show) => !show)}
                tabIndex={4}
              >
                {!showAdvanced ? "+" : "—"}{" "}
                {!showAdvanced ? "Add more" : "Less"}
              </Button>
            </div>
          )}
        </div>
        {children && childrenPosition === "right" ? children : null}
      </div>
      <AnimatePresence>
        {showAdvanced && (
          <motion.div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
            initial={{ height: 0, opacity: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            key="advanced"
          >
            <Box>
              <Typography sx={{ mb: 1 }}>
                <strong>Business Information</strong>
              </Typography>
              <Stack
                sx={{
                  gap: "1em",
                }}
              >
                <TextField
                  fullWidth
                  name="businessName"
                  onChange={advancedSearchForm.handleChange}
                  value={advancedSearchForm.values.businessName}
                  label="Name"
                  type="text"
                  slotProps={{
                    htmlInput: { tabIndex: 5 },
                  }}
                />
                <TextField
                  sx={{ mt: 3 }}
                  fullWidth
                  name="addressLine1"
                  onChange={advancedSearchForm.handleChange}
                  value={advancedSearchForm.values.addressLine1}
                  label="Address Line 1"
                  type="text"
                  slotProps={{
                    htmlInput: { tabIndex: 6 },
                  }}
                />
                <TextField
                  fullWidth
                  name="addressLine2"
                  onChange={advancedSearchForm.handleChange}
                  value={advancedSearchForm.values.addressLine2}
                  label="Address Line 2"
                  type="text"
                  slotProps={{
                    htmlInput: { tabIndex: 7 },
                  }}
                />
                <Box sx={{ display: "flex", gap: "1em" }}>
                  <TextField
                    fullWidth
                    name="city"
                    onChange={advancedSearchForm.handleChange}
                    value={advancedSearchForm.values.city}
                    label="City"
                    type="text"
                    slotProps={{
                      htmlInput: { tabIndex: 8 },
                    }}
                  />
                  <TextField
                    fullWidth
                    name="stateProvince"
                    onChange={advancedSearchForm.handleChange}
                    value={advancedSearchForm.values.stateProvince}
                    label="State/Province"
                    type="text"
                    slotProps={{
                      htmlInput: { tabIndex: 9 },
                    }}
                  />
                </Box>
                <Box sx={{ display: "flex", gap: "1em" }}>
                  <TextField
                    fullWidth
                    name="postalCode"
                    onChange={advancedSearchForm.handleChange}
                    value={advancedSearchForm.values.postalCode}
                    label="Postal Code"
                    type="text"
                    slotProps={{
                      htmlInput: { tabIndex: 10 },
                    }}
                  />
                  <TextField
                    fullWidth
                    name="country"
                    onChange={advancedSearchForm.handleChange}
                    value={advancedSearchForm.values.country}
                    label="Country"
                    type="text"
                    slotProps={{
                      htmlInput: { tabIndex: 11 },
                    }}
                  />
                </Box>
                <TextField
                  sx={{
                    mt: 3,
                    ".MuiInputBase-inputMultiline": {
                      resize: "vertical",
                    },
                  }}
                  multiline
                  minRows={3}
                  fullWidth
                  name="description"
                  onChange={advancedSearchForm.handleChange}
                  value={advancedSearchForm.values.description}
                  label="Description"
                  type="text"
                  slotProps={{
                    htmlInput: { tabIndex: 12 },
                  }}
                />
              </Stack>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography sx={{ mb: 1 }}>
                <strong>Applicant Information</strong>
              </Typography>

              <TextField
                fullWidth
                name="applicantFullName"
                onChange={advancedSearchForm.handleChange}
                value={advancedSearchForm.values.applicantFullName}
                label="Full Name"
                type="text"
                slotProps={{
                  htmlInput: { tabIndex: 13 },
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  gap: "1.25em",
                  mt: "1em",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flex: 1,
                    flexDirection: "column",
                    gap: "1em",
                  }}
                >
                  <TextField
                    fullWidth
                    name="email"
                    onChange={advancedSearchForm.handleChange}
                    value={advancedSearchForm.values.email}
                    label="Email"
                    type="text"
                    slotProps={{
                      htmlInput: { tabIndex: 14 },
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flex: 1,
                    flexDirection: "column",
                    gap: "1em",
                  }}
                >
                  <TextField
                    fullWidth
                    name="phone"
                    onChange={advancedSearchForm.handleChange}
                    value={advancedSearchForm.values.phone}
                    label="Phone"
                    type="text"
                    slotProps={{
                      htmlInput: { tabIndex: 15 },
                    }}
                  />
                </Box>
              </Box>
            </Box>

            <Box sx={{ mt: 2 }}>
              <details>
                <summary style={{ cursor: "pointer" }}>
                  <Typography sx={{ display: "inline" }}>
                    <strong>Additional Options</strong>
                  </Typography>
                </summary>
                <div style={{ marginTop: "1em" }}>
                  <div>
                    <TextField
                      fullWidth
                      name="externalTrackingRef"
                      onChange={advancedSearchForm.handleChange}
                      value={advancedSearchForm.values.externalTrackingRef}
                      label="Reference ID"
                      type="text"
                      slotProps={{
                        htmlInput: { tabIndex: 16 },
                      }}
                    />
                  </div>
                </div>
                <div style={{ marginTop: "1em" }}>
                  <div>
                    {/* There's a bug in our version of MUI where the select label is invisible for some reason */}
                    {/* this attempts to at least semi-fake it */}
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: theme.palette.grey[600],
                        paddingLeft: "1em",
                        transform: "translateY(50%)",
                        position: "relative",
                        zIndex: 999,
                      }}
                    >
                      <span
                        style={{
                          backgroundColor: "white",
                          paddingLeft: "0.5em",
                          paddingRight: "0.5em",
                        }}
                      >
                        Risk Profile
                      </span>
                    </Typography>
                    <Select
                      title="Risk Profile"
                      label="RiskProfile"
                      value={matchProfileId}
                      onChange={(e) => {
                        setMatchProfileId(e.target.value);
                      }}
                      inputProps={{
                        tabIndex: 17,
                      }}
                      displayEmpty
                      fullWidth
                    >
                      <MenuItem value="" key="default">
                        TrueBiz Default
                      </MenuItem>
                      {(matchProfiles || []).map((p) => {
                        if (p.id) {
                          return (
                            <MenuItem key={p.id} value={p.id}>
                              {p.name.replace(/\([0-9a-fA-f-]+\)/, "")}
                            </MenuItem>
                          );
                        }
                        return null;
                      })}
                    </Select>
                  </div>
                </div>
              </details>
            </Box>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "2em",
              }}
            >
              <Button
                startIcon={<SearchOutlinedIcon />}
                variant="contained"
                sx={{
                  textTransform: "unset",
                  fontSize: "unset",
                }}
                tabIndex={19}
                type="submit"
              >
                Search
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </form>
  );
});

export default SearchBar;
